import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Box, Typography, IconButton, Tooltip, CircularProgress } from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  ArrowBackOutlined, SaveOutlined,
  CheckCircleOutlined, ErrorOutlined,
  UndoOutlined, RedoOutlined,
  StorefrontOutlined, GestureOutlined,
} from '@mui/icons-material';
import { api } from '../../../hooks/useApi'; // adjust to your actual path

import { PRIMARY, CANVAS_W, CANVAS_H, ASPECT, SHAPE_TYPES, uid, pct } from './constants';

import { DragGhost }                     from './canvas/DragGhost';
import { usePenTool, usePathEdit }       from './canvas/PenTool';
import { applyResize, translateHotspot } from './canvas/SelectionGizmo';
import { LobbyCanvas }                   from './canvas/LobbyCanvas';
import { HotspotInspector }             from './panels/HotspotInspector';
import { CanvasInspector }              from './panels/CanvasInspector';
import { LayersPanel }                  from './panels/LayersPanel';

// ── Save status button ────────────────────────────────────────────────────────
function SaveBtn({ status, onSave }) {
  const cfg =
    status === 'saved'  ? { bg: alpha('#22c55e', 0.1), color: '#16a34a', border: '#86efac', icon: <CheckCircleOutlined sx={{ fontSize: 14 }} />, label: 'Saved'   } :
    status === 'error'  ? { bg: alpha('#ef4444', 0.1), color: '#dc2626', border: '#fca5a5', icon: <ErrorOutlined    sx={{ fontSize: 14 }} />, label: 'Error'   } :
    status === 'saving' ? { bg: '#f0f4f8', color: '#9ea8b3', border: '#e4e9f0', icon: <SaveOutlined sx={{ fontSize: 14 }} />, label: 'Saving…' } :
                          { bg: '#f0f4f8', color: '#5a6474', border: '#e4e9f0', icon: <SaveOutlined sx={{ fontSize: 14 }} />, label: 'Save',
                            hover: { background: alpha(PRIMARY, 0.06), color: PRIMARY, borderColor: alpha(PRIMARY, 0.3) } };
  return (
    <Box
      onClick={status !== 'saving' ? onSave : undefined}
      sx={{
        display: 'flex', alignItems: 'center', gap: 0.5,
        px: 1.25, height: 30, borderRadius: 1.5,
        cursor: status === 'saving' ? 'default' : 'pointer',
        fontSize: '0.75rem', fontWeight: 600, transition: 'all 0.15s',
        background: cfg.bg, color: cfg.color,
        border: '1px solid', borderColor: cfg.border,
        '&:hover': cfg.hover || {},
      }}
    >
      {cfg.icon}
      <Box component="span">{cfg.label}</Box>
    </Box>
  );
}

// ── Booth editor toolbar ──────────────────────────────────────────────────────
function BoothEditorToolbar({ exhibitor, hist, saveStatus, onBack, onUndo, onRedo, onSave }) {
  return (
    <Box sx={{
      background: '#fff', borderBottom: '1px solid #e8edf2',
      px: 2, height: 48,
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      flexShrink: 0,
    }}>
      {/* Left: back + history + save */}
      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
        <Tooltip title="Back to exhibitors" placement="bottom">
          <IconButton size="small" onClick={onBack} sx={{ mr: 0.5 }}>
            <ArrowBackOutlined sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>

        <Box sx={{ width: 1, height: 22, bgcolor: '#e8edf2', mx: 0.5 }} />

        <Tooltip title="Undo">
          <IconButton size="small" onClick={onUndo} disabled={hist.idx === 0}>
            <UndoOutlined sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Redo">
          <IconButton size="small" onClick={onRedo} disabled={hist.idx >= hist.stack.length - 1}>
            <RedoOutlined sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
        <SaveBtn status={saveStatus} onSave={onSave} />
      </Box>

      {/* Center: exhibitor name */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <StorefrontOutlined sx={{ fontSize: 14, color: '#b0bac8' }} />
        <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#3c4555' }}>
          {exhibitor?.name || 'Booth Editor'}
        </Typography>
        <Box sx={{
          fontSize: '0.6rem', fontWeight: 700, px: 0.75, py: 0.2, borderRadius: '4px',
          background: alpha(PRIMARY, 0.1), color: PRIMARY, letterSpacing: '0.05em',
        }}>
          BOOTH
        </Box>
        <Typography sx={{ fontSize: '0.68rem', color: '#b0bac8', fontFamily: 'monospace' }}>
          {CANVAS_W} × {CANVAS_H}
        </Typography>
      </Box>

      {/* Right: symmetry spacer */}
      <Box sx={{ width: 160 }} />
    </Box>
  );
}

// ── Default left-panel hint ───────────────────────────────────────────────────
function DrawHint() {
  return (
    <Box sx={{
      flex: 1, display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      gap: 1.5, p: 3, textAlign: 'center',
    }}>
      <Box sx={{
        width: 48, height: 48, borderRadius: '12px',
        background: alpha(PRIMARY, 0.08),
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <GestureOutlined sx={{ fontSize: 26, color: alpha(PRIMARY, 0.5) }} />
      </Box>
      <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#3c4555' }}>
        Draw or select a shape
      </Typography>
      <Typography sx={{ fontSize: '0.71rem', color: '#9ea8b3', lineHeight: 1.6 }}>
        Use the toolbar below the canvas to draw a rectangle, circle, or custom bezier shape.
        Select an existing hotspot to configure it.
      </Typography>
    </Box>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// Schema translation helpers
//
// The Exhibitor schema stores hotspot behaviour in a flat `action` object:
//   { type: 'image'|'video'|'pdf'|'game'|'iframe', imageUrl, videoUrl, ... }
//
// The HotspotInspector works with a richer shape:
//   { hotspotType: 'link'|'video'|'gallery'|'iframe', hotspotAction: { ... } }
//
// These two functions translate between the two formats so the editor UI stays
// decoupled from the DB schema.
// ══════════════════════════════════════════════════════════════════════════════

/**
 * DB hotspot → editor hotspot
 * Called once on load to convert each hotspot coming from the API.
 */
function deserializeHotspot(h) {
  const a = h.action || {};
  let hotspotType   = 'link';
  let hotspotAction = {};

  switch (a.type) {
    case 'video':
      hotspotType = 'video';
      hotspotAction = {
        videoProvider: 'url',
        videoUrl:      a.videoUrl || '',
        autoplay:      false,
        controls:      true,
      };
      break;
    case 'image': {
      // Restore full gallery from meta.galleryImages if available,
      // otherwise fall back to the single imageUrl stored in action.
      const savedImages = h.meta?.galleryImages;
      hotspotType = 'gallery';
      hotspotAction = {
        images: savedImages?.length
          ? savedImages
          : a.imageUrl
            ? [{ url: a.imageUrl, caption: a.imageCaption || '' }]
            : [],
      };
      break;
    }
    case 'pdf':
      // PDF → iFrame pop-up (display inline via embed code)
      hotspotType = 'iframe';
      hotspotAction = {
        embedCode:  a.pdfUrl || '',
        modalWidth: 'lg',
      };
      break;
    case 'iframe':
      hotspotType = 'iframe';
      hotspotAction = {
        embedCode:  a.iframeSrc || '',
        modalWidth: 'md',
      };
      break;
    case 'link':
      hotspotType = 'link';
      hotspotAction = {
        linkTarget:  'external',
        externalUrl: a.linkUrl    || '',
        openIn:      a.linkTarget === '_blank' ? 'blank' : 'same',
      };
      break;
    case 'game':
    default:
      hotspotType = 'link';
      hotspotAction = {
        linkTarget:  'external',
        externalUrl: '',
        openIn:      'same',
      };
      break;
  }

  return { ...h, hotspotType, hotspotAction };
}

/**
 * Editor hotspot → DB hotspot
 * Called on save to strip UI-only fields and rebuild the `action` object.
 */
function serializeHotspot(h) {
  const { hotspotType, hotspotAction = {}, ...rest } = h;

  let action;
  switch (hotspotType) {
    case 'video':
      action = {
        type:       'video',
        videoUrl:   hotspotAction.videoUrl   || null,
        videoTitle: null,
      };
      break;
    case 'gallery': {
      // Persist first image in the schema's imageUrl slot;
      // full array is kept in meta so nothing is lost on round-trip.
      const first = hotspotAction.images?.[0];
      action = {
        type:         'image',
        imageUrl:     first?.url     || null,
        imageCaption: first?.caption || null,
      };
      // Stash full gallery in meta for lossless round-trip
      rest.meta = { ...(rest.meta || {}), galleryImages: hotspotAction.images || [] };
      break;
    }
    case 'iframe':
      action = {
        type:      'iframe',
        iframeSrc: hotspotAction.embedCode || null,
      };
      break;
    case 'link':
    default:
      // External URL link — stored as type 'link' in the schema
      action = {
        type:       'link',
        linkUrl:    hotspotAction.externalUrl || null,
        linkTarget: hotspotAction.openIn === 'blank' ? '_blank' : '_self',
      };
      break;
  }

  return { ...rest, action };
}

// ══════════════════════════════════════════════════════════════════════════════
// ExhibitorBoothEditor
// ══════════════════════════════════════════════════════════════════════════════
/**
 * Props:
 *   exhibitor  — Exhibitor document being edited
 *   onBack     — () => void — return to ExhibitorCanvas grid
 */
export function ExhibitorBoothEditor({ exhibitor: exhibitorProp, onBack }) {

  // ── Fetch latest exhibitor data on mount ─────────────────────────────────────
  // The prop comes from the grid and may be stale (e.g. hotspots saved in a
  // previous session). Always fetch fresh from the API before initialising state.
  const [loading,   setLoading]   = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [exhibitor,  setExhibitor]  = useState(null);

  useEffect(() => {
    if (!exhibitorProp?._id) { setLoading(false); return; }
    api.get(`/exhibitors/${exhibitorProp._id}`)
      .then(({ data: res }) => {
        if (res.success) setExhibitor(res.data);
        else setFetchError('Failed to load booth data.');
      })
      .catch(() => setFetchError('Failed to load booth data.'))
      .finally(() => setLoading(false));
  }, [exhibitorProp?._id]);

  // ── Hotspot state + history ─────────────────────────────────────────────────
  // Initialised once the fetch resolves — see the useEffect below.
  const [hotspots,   setHotspots]   = useState([]);
  const [hist,       setHist]       = useState({ stack: [[]], idx: 0 });
  const [selectedId, setSelectedId] = useState(null);
  const [saveStatus, setSaveStatus] = useState('idle');

  // Seed state when the fresh exhibitor arrives
  useEffect(() => {
    if (!exhibitor) return;
    const initial = (exhibitor.hotspots || []).map(deserializeHotspot);
    setHotspots(initial);
    setHist({ stack: [initial], idx: 0 });
  }, [exhibitor]);

  // Tool state — mirrors LobbyBuilderTab exactly
  const [activeTool, setActiveTool] = useState('pointer'); // 'pointer' | 'pen' | 'rect' | 'circle'
  const [drawState,  setDrawState]  = useState(null);      // rect/circle drag preview state

  // Path edit state (double-click a bezier shape to enter node editing)
  const [editingHotspot, setEditingHotspot] = useState(null);

  const canvasRef   = useRef(null);
  const dragTypeRef = useRef(null); // kept for LobbyCanvas prop compatibility (no palette drag in booth)
  const [ghost, setGhost] = useState(null);

  // Synthesise a lobby-like object so LobbyCanvas renders the background.
  // Derived from fetched exhibitor so it always reflects the latest saved bg.
  const [localBg, setLocalBg] = useState(null);

  useEffect(() => {
    if (!exhibitor) return;
    setLocalBg({
      bgUrl:   exhibitor.boothBgImage || exhibitor.boothBgVideo || null,
      bgType:  exhibitor.boothBgVideo ? 'video' : exhibitor.boothBgImage ? 'image' : 'color',
      bgColor: exhibitor.boothBgColor || '#0D1B2A',
    });
  }, [exhibitor]);

  const fakeLobby = {
    _id:   exhibitor?._id,
    name:  exhibitor?.name,
    pages: [],
    ...(localBg || {
      bgUrl:   null,
      bgType:  'color',
      bgColor: '#0D1B2A',
    }),
  };

  // ── History helpers ─────────────────────────────────────────────────────────
  const pushHistory = useCallback((next) => {
    setHotspots(next);
    setHist(h => {
      const stack = h.stack.slice(0, h.idx + 1).concat([next]);
      return { stack, idx: stack.length - 1 };
    });
  }, []);

  const undo = useCallback(() => {
    if (hist.idx === 0) return;
    setHotspots(hist.stack[hist.idx - 1]);
    setHist(h => ({ ...h, idx: h.idx - 1 }));
  }, [hist]);

  const redo = useCallback(() => {
    if (hist.idx >= hist.stack.length - 1) return;
    setHotspots(hist.stack[hist.idx + 1]);
    setHist(h => ({ ...h, idx: h.idx + 1 }));
  }, [hist]);

  const buildPayload = useCallback((lobby, hs) => ({
    name:               lobby.name,
    slug:               lobby.slug,
    isDefault:          lobby.isDefault          ?? false,
    isActive:           lobby.isActive           ?? true,
    bgType:             lobby.bgType             || 'image',
    boothBgImage:       lobby.bgUrl              || null,
    showIntro:          lobby.showIntro          ?? true,
    introVideoUrl:      lobby.introVideoUrl      || null,
    transitionVideoUrl: lobby.transitionVideoUrl || null,
    returnVideoUrl:     lobby.returnVideoUrl     || null,
    event:              event._id,
    hotspots:           hs,
  }), [event?._id]);
  

  // ── Save ────────────────────────────────────────────────────────────────────
  const save = useCallback(async () => {
    if (!exhibitor?._id) return;
    setSaveStatus('saving');
    try {
      const serialized = hotspots.map(serializeHotspot);
      const payload = buildPayload(localBg, serialized);
      console.log(localBg)

      await api.put(`/exhibitors/${exhibitor._id}`, {...localBg, hotspots: serialized });
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch {
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
    }
  }, [exhibitor?._id, hotspots]);

  // ── Derived ─────────────────────────────────────────────────────────────────
  const canvasSelected  = selectedId === 'canvas';
  const selectedHotspot = (selectedId && selectedId !== 'canvas')
    ? hotspots.find(h => h.id === selectedId) || null
    : null;

  // ── Hotspot CRUD ────────────────────────────────────────────────────────────
  const handleUpdateHotspot = useCallback((updated) => {
    pushHistory(hotspots.map(h => h.id === updated.id ? updated : h));
  }, [hotspots, pushHistory]);

  const handleDeleteHotspot = useCallback((id) => {
    pushHistory(hotspots.filter(h => h.id !== id));
    setSelectedId(null);
  }, [hotspots, pushHistory]);

  const handleUpdateLobby = useCallback((patch) => {
    setLocalBg(prev => ({ ...prev, ...patch }));
  }, []);

  // ── Canvas coord helpers ─────────────────────────────────────────────────────
  // Returns { x, y } in % (0–100) — used by pen tool and move/resize
  const getCanvasCoords = useCallback((e) => {
    const el = canvasRef.current;
    if (!el) return null;
    const rect = el.getBoundingClientRect();
    return {
      x: pct(e.clientX - rect.left, rect.width),
      y: pct(e.clientY - rect.top,  rect.height),
    };
  }, []);

  // Returns { x, y } in raw SVG viewBox units — used by rect/circle draw preview
  const getSvgCoords = useCallback((e) => {
    const el = canvasRef.current;
    if (!el) return null;
    const rect = el.getBoundingClientRect();
    return {
      x: ((e.clientX - rect.left) / rect.width)  * CANVAS_W,
      y: ((e.clientY - rect.top)  / rect.height) * CANVAS_H,
    };
  }, []);

  // ── Pen tool ─────────────────────────────────────────────────────────────────
  const penTool = usePenTool({
    getCanvasCoords,
    onCommit: useCallback((hotspot) => {
      const h = {
        ...hotspot,
        id:          uid(),
        order:       hotspots.length,
        hotspotType: 'link',
        hotspotAction: {},
      };
      pushHistory([...hotspots, h]);
      setSelectedId(h.id);
      setActiveTool('pointer');
    }, [hotspots, pushHistory]),
  });

  // ── Path edit (double-click a bezier shape) ──────────────────────────────────
  const pathEdit = usePathEdit({
    getCanvasCoords,
    hotspot: editingHotspot,
    onCommit: useCallback((updated) => {
      // Live update on every drag tick — no history push to avoid flooding
      setHotspots(prev => prev.map(h => h.id === updated.id ? updated : h));
    }, []),
    onExit: useCallback(() => {
      // Push a single history entry when the user exits path edit mode
      setHotspots(hs => { pushHistory(hs); return hs; });
      setEditingHotspot(null);
    }, [pushHistory]),
  });

  // Allow LobbyCanvas to trigger path edit entry on double-click
  pathEdit.onEnter = useCallback((hotspot) => {
    setEditingHotspot(hotspot);
    setSelectedId(null);
  }, []);

  // Exit path edit when switching away from pointer tool
  useEffect(() => {
    if (activeTool !== 'pointer' && editingHotspot) setEditingHotspot(null);
  }, [activeTool, editingHotspot]);

  // Keyboard shortcuts: P = pen, Escape = cancel active tool
  useEffect(() => {
    const onKey = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      if (e.key === 'p' || e.key === 'P') {
        setActiveTool(t => t === 'pen' ? 'pointer' : 'pen');
      }
      if (e.key === 'Escape') {
        if (activeTool === 'pen') { penTool.reset(); setActiveTool('pointer'); }
        if (activeTool === 'rect' || activeTool === 'circle') {
          setDrawState(null);
          setActiveTool('pointer');
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [activeTool, penTool]);

  // ── Tool selection ───────────────────────────────────────────────────────────
  const handleSelectTool = useCallback((key) => {
    if (key !== 'pen') penTool.reset();
    setActiveTool(key);
    setSelectedId(null);
    setDrawState(null);
  }, [penTool]);

  // ── Rect / Circle draw ───────────────────────────────────────────────────────
  const handleDrawMouseDown = useCallback((e) => {
    if (activeTool !== 'rect' && activeTool !== 'circle') return;
    e.stopPropagation();
    const pt = getSvgCoords(e);
    if (!pt) return;
    setDrawState({ active: true, startX: pt.x, startY: pt.y, curX: pt.x, curY: pt.y });
  }, [activeTool, getSvgCoords]);

  const handleDrawMouseMove = useCallback((e) => {
    if (!drawState?.active) return;
    const pt = getSvgCoords(e);
    if (!pt) return;
    setDrawState(prev => ({ ...prev, curX: pt.x, curY: pt.y }));
  }, [drawState, getSvgCoords]);

  const handleDrawMouseUp = useCallback(() => {
    if (!drawState?.active) { setDrawState(null); return; }
    const { startX, startY, curX, curY } = drawState;
    setDrawState(null);

    const dx = Math.abs(curX - startX);
    const dy = Math.abs(curY - startY);
    if (dx < 2 && dy < 2) return; // ignore accidental click-without-drag

    const id = uid();
    let h;

    if (activeTool === 'rect') {
      const x  = pct(Math.min(startX, curX), CANVAS_W);
      const y  = pct(Math.min(startY, curY), CANVAS_H);
      const w  = pct(dx, CANVAS_W);
      const ht = pct(dy, CANVAS_H);
      h = {
        id, label: 'Rectangle Hotspot', shape: 'rect',
        points: [{ x, y }],
        meta: { width: w, height: ht },
        hotspotType: 'link', hotspotAction: {},
        glowColor: SHAPE_TYPES.rect.color, glowOpacity: 0.5,
        order: hotspots.length, isActive: true,
      };
    } else {
      // Circle — center is mousedown point, radius is drag distance
      const cx = pct(startX, CANVAS_W);
      const cy = pct(startY, CANVAS_H);
      const r  = pct(Math.sqrt(dx * dx + dy * dy), CANVAS_W);
      h = {
        id, label: 'Circle Hotspot', shape: 'circle',
        points: [{ x: cx, y: cy }],
        meta: { radius: r },
        hotspotType: 'link', hotspotAction: {},
        glowColor: SHAPE_TYPES.circle.color, glowOpacity: 0.5,
        order: hotspots.length, isActive: true,
      };
    }

    pushHistory([...hotspots, h]);
    setSelectedId(h.id);
    setActiveTool('pointer');
  }, [drawState, activeTool, hotspots, pushHistory]);

  // ── Hotspot drag-move ────────────────────────────────────────────────────────
  const startHotspotMove = useCallback((e, id) => {
    const start = getCanvasCoords(e);
    if (!start) return;
    const snap = JSON.parse(JSON.stringify(hotspots));
    const onMove = (ev) => {
      const cur = getCanvasCoords(ev);
      if (!cur) return;
      const dx = cur.x - start.x, dy = cur.y - start.y;
      setHotspots(snap.map(h => h.id === id ? translateHotspot(h, dx, dy) : h));
    };
    const onUp = () => {
      setHotspots(hs => { pushHistory(hs); return hs; });
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup',   onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup',   onUp);
  }, [getCanvasCoords, hotspots, pushHistory]);

  const handleHotspotMouseDown = useCallback((e, id) => {
    e.stopPropagation();
    setSelectedId(id);
    startHotspotMove(e, id);
  }, [startHotspotMove]);

  const handleGizmoBodyMouseDown = useCallback((e) => {
    e.stopPropagation();
    if (selectedId && selectedId !== 'canvas') startHotspotMove(e, selectedId);
  }, [selectedId, startHotspotMove]);

  // ── Resize handle drag ───────────────────────────────────────────────────────
  const handleHandleMouseDown = useCallback((e, handleKey) => {
    e.stopPropagation();
    e.preventDefault();
    if (!selectedId || selectedId === 'canvas') return;
    const hotspot = hotspots.find(h => h.id === selectedId);
    if (!hotspot) return;
    const startCoords = getCanvasCoords(e);
    if (!startCoords) return;
    const original = JSON.parse(JSON.stringify(hotspot));
    const onMove = (ev) => {
      const cur = getCanvasCoords(ev);
      if (!cur) return;
      const resized = applyResize(original, handleKey, cur.x - startCoords.x, cur.y - startCoords.y);
      setHotspots(prev => prev.map(h => h.id === selectedId ? resized : h));
    };
    const onUp = () => {
      setHotspots(hs => { pushHistory(hs); return hs; });
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup',   onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup',   onUp);
  }, [selectedId, hotspots, getCanvasCoords, pushHistory]);

  // ── Render ───────────────────────────────────────────────────────────────────

  // Loading / error gates — shown before the canvas is ready
  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', background: '#f0f4f8' }}>
        <BoothEditorToolbar
          exhibitor={exhibitorProp}
          hist={{ stack: [[]], idx: 0 }}
          saveStatus="idle"
          onBack={onBack}
          onUndo={() => {}} onRedo={() => {}} onSave={() => {}}
        />
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, flexDirection: 'column' }}>
          <CircularProgress size={28} sx={{ color: PRIMARY }} />
          <Typography sx={{ fontSize: '0.8rem', color: '#9ea8b3' }}>Loading booth…</Typography>
        </Box>
      </Box>
    );
  }

  if (fetchError || !exhibitor) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', background: '#f0f4f8' }}>
        <BoothEditorToolbar
          exhibitor={exhibitorProp}
          hist={{ stack: [[]], idx: 0 }}
          saveStatus="idle"
          onBack={onBack}
          onUndo={() => {}} onRedo={() => {}} onSave={() => {}}
        />
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
          <Typography sx={{ fontSize: '0.8rem', color: '#dc2626' }}>
            {fetchError || 'Booth not found.'}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', height: '100%', background: '#f0f4f8' }}
      onClick={e => {
        if (e.target.closest && e.target.closest('svg')) return;
        setSelectedId(null);
      }}
    >
      <DragGhost ghost={ghost} />

      <BoothEditorToolbar
        exhibitor={exhibitor}
        hist={hist}
        saveStatus={saveStatus}
        onBack={onBack}
        onUndo={undo}
        onRedo={redo}
        onSave={save}
      />

      <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

        {/* ── Left panel ── */}
        <Box
          onClick={e => e.stopPropagation()}
          sx={{
            width: 230, flexShrink: 0,
            borderRight: '1px solid #e8edf2',
            background: '#fafbfc',
            overflow: 'hidden',
            display: 'flex', flexDirection: 'column',
          }}
        >
          {selectedHotspot ? (
            <HotspotInspector
              hotspot={selectedHotspot}
              lobbies={[]}
              plenaries={[]}
              onUpdate={handleUpdateHotspot}
              onDelete={() => handleDeleteHotspot(selectedHotspot.id)}
              onClose={() => setSelectedId(null)}
            />
          ) : canvasSelected ? (
            <CanvasInspector
              lobby={fakeLobby}
              onUpdateLobby={handleUpdateLobby}
              onClose={() => setSelectedId(null)}
            />
          ) : (
            <DrawHint />
          )}
        </Box>

        {/* ── Canvas ── */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <LobbyCanvas
            canvasRef={canvasRef}
            lobby={fakeLobby}
            hotspots={hotspots}
            selectedId={selectedId}
            canvasSelected={canvasSelected}
            previewMode={false}
            ghost={ghost}
            dragTypeRef={dragTypeRef}
            onCanvasClick={e => {
              if (e.target.closest && e.target.closest('svg')) return;
              e.stopPropagation();
              setSelectedId('canvas');
            }}
            onCanvasDragOver={e => e.preventDefault()}
            onCanvasDrop={() => {}}
            onHotspotMouseDown={handleHotspotMouseDown}
            onHotspotClick={id => setSelectedId(id)}
            onHandleMouseDown={handleHandleMouseDown}
            onGizmoBodyMouseDown={handleGizmoBodyMouseDown}
            activeTool={activeTool}
            onSelectTool={handleSelectTool}
            penTool={penTool}
            pathEdit={pathEdit}
            drawState={drawState}
            onDrawMouseDown={handleDrawMouseDown}
            onDrawMouseMove={handleDrawMouseMove}
            onDrawMouseUp={handleDrawMouseUp}
          />
        </Box>

        {/* ── Right panel: layers ── */}
        <LayersPanel
          hotspots={hotspots}
          selectedId={selectedId}
          onSelect={setSelectedId}
          onDelete={handleDeleteHotspot}
        />
      </Box>
    </Box>
  );
}